import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import LoginModal from '@/components/feature/LoginModal';
import {
  clearPortalUser,
  getPortalUser,
  onUnauthorized,
  setPortalUser,
  type PortalUser,
} from '@/services/request';

interface AuthContextValue {
  /** 当前登录用户（未登录为 null） */
  user: PortalUser | null;
  /** 打开登录弹窗 */
  openLogin: () => void;
  /** 退出登录 */
  logout: () => void;
  /**
   * 需要登录才能执行的动作：已登录时直接执行并返回 true；
   * 未登录时打开登录弹窗，登录成功后自动执行 action，返回 false。
   */
  requireLogin: (action?: () => void) => boolean;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  openLogin: () => {},
  logout: () => {},
  requireLogin: () => false,
});

/**
 * 登录态上下文：统一管理会员登录/退出与登录弹窗。
 * 登录弹窗挂载在 Provider 内，Navbar、报名/咨询表单等通过 openLogin / requireLogin 复用。
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(() => getPortalUser());
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginNotice, setLoginNotice] = useState('');
  const pendingActionRef = useRef<(() => void) | null>(null);

  // token 失效（401/403）时自动弹出登录弹窗
  useEffect(() => {
    onUnauthorized((message) => {
      pendingActionRef.current = null;
      setLoginNotice(message);
      setLoginOpen(true);
    });
    return () => onUnauthorized(null);
  }, []);

  const openLogin = useCallback(() => {
    setLoginNotice('');
    setLoginOpen(true);
  }, []);

  const closeLogin = useCallback(() => {
    pendingActionRef.current = null;
    setLoginNotice('');
    setLoginOpen(false);
  }, []);

  const logout = useCallback(() => {
    clearPortalUser();
    setUser(null);
  }, []);

  const handleLoginSuccess = useCallback((portalUser: PortalUser) => {
    setPortalUser(portalUser);
    setUser(portalUser);
    setLoginOpen(false);
    setLoginNotice('');
    // 登录前被拦截的动作（报名/咨询提交），登录成功后自动继续
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  }, []);

  const requireLogin = useCallback((action?: () => void) => {
    if (getPortalUser()) {
      action?.();
      return true;
    }
    pendingActionRef.current = action ?? null;
    setLoginNotice('');
    setLoginOpen(true);
    return false;
  }, []);

  const value = useMemo(
    () => ({ user, openLogin, logout, requireLogin }),
    [user, openLogin, logout, requireLogin],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <LoginModal open={loginOpen} notice={loginNotice} onClose={closeLogin} onSuccess={handleLoginSuccess} />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
